/**
 * Site detail — the gateways at a site and their enrolment state, with the two operations that keep
 * a site coming online: register another gateway, and (re)issue a gateway's mTLS certificate. The
 * cert bundle is shown exactly once and offered as a download, because the API returns the gateway's
 * private key a single time and never again.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth';
import { ApiError, type CertBundle, type EnrolToken, type Gateway } from '../api';
import { Banner, Button, CommissioningChain, EnrolTokenCard, GatewayState } from '../ui';

/**
 * Whether a RutOS version is inside Teltonika's RED Article 3.3(d) declaration, which holds for 7.15
 * and later. Compared numerically part by part so 7.9 reads as OLDER than 7.15 — the comparison a
 * string sort gets backwards, and the one that decides compliant from not.
 */
function redCompliant(version: string | null): boolean | null {
  if (!version) return null;
  const m = version.match(/(\d+(?:\.\d+)+)/);
  if (!m) return null;
  const a = m[1]!.split('.').map(Number);
  const b = [7, 15];
  // Teltonika ships versions as RUT9_R_00.07.15.2; the fixed "00." prefix would read as major
  // version 0 and make every router look pre-7.15. Mirrors meetsMinimum() in edge/backhaul/firmware.
  while (a.length > 2 && a[0] === 0) a.shift();
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = a[i] ?? 0;
    const y = b[i] ?? 0;
    if (x !== y) return x > y;
  }
  return true;
}

export function SiteDetail(): JSX.Element {
  const { api, session } = useAuth();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const [gateways, setGateways] = useState<Gateway[] | null>(null);
  const [enrol, setEnrol] = useState<EnrolToken | null>(null);
  const [mintingId, setMintingId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [serial, setSerial] = useState('');
  const [adding, setAdding] = useState(false);
  const [issuingId, setIssuingId] = useState<string | null>(null);
  const [bundle, setBundle] = useState<CertBundle | null>(null);

  const admin = session?.role === 'tenant_admin';

  const load = useCallback(() => {
    api
      .listGateways(id)
      .then((r) => setGateways(r.gateways))
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'Could not load gateways.'));
  }, [api, id]);

  useEffect(load, [load]);

  async function addGateway(e: React.FormEvent) {
    e.preventDefault();
    setAdding(true);
    setErr(null);
    try {
      await api.createGateway(id, serial.trim());
      setSerial('');
      load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not register the gateway.');
    } finally {
      setAdding(false);
    }
  }

  async function issue(gw: Gateway) {
    setIssuingId(gw.id);
    setErr(null);
    setBundle(null);
    try {
      const b = await api.issueCert(gw.id);
      setBundle(b);
      load();
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? e.status === 503
            ? 'No certificate authority is configured on the server yet, so certs cannot be issued.'
            : e.message
          : 'Could not issue the certificate.',
      );
    } finally {
      setIssuingId(null);
    }
  }

  async function mintToken(gw: Gateway) {
    setMintingId(gw.id);
    setErr(null);
    setEnrol(null);
    try {
      setEnrol(await api.issueEnrolToken(gw.id));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not mint the enrolment token.');
    } finally {
      setMintingId(null);
    }
  }

  async function decommissionGateway(gw: Gateway) {
    if (!window.confirm(`Decommission gateway ${gw.serial}? This removes it and its controllers.`))
      return;
    setErr(null);
    try {
      await api.decommissionGateway(gw.id);
      load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not decommission the gateway.');
    }
  }

  async function decommissionSite() {
    if (
      !window.confirm(
        'Decommission this site? This permanently removes its gateways, controllers and history.',
      )
    )
      return;
    setErr(null);
    try {
      await api.decommissionSite(id);
      navigate('/sites', { replace: true });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not decommission the site.');
    }
  }

  return (
    <>
      <div className="crumbs">
        <Link to="/sites">Sites</Link> <span className="faint">/</span> this site
      </div>
      <div className="page-head">
        <div>
          <h1>Site gateways</h1>
          <div className="lead">
            Register gateways and issue their certificates to bring them online.
          </div>
        </div>
        <div className="row-right">
          <Link className="btn btn-ghost" to={`/sites/${id}/controllers/new`}>
            + Add controller
          </Link>
          {admin && (
            <button className="btn btn-ghost btn-sm" onClick={decommissionSite}>
              Decommission site
            </button>
          )}
        </div>
      </div>

      {err && <Banner kind="err">{err}</Banner>}
      {bundle && <CertBundleCard bundle={bundle} onDismiss={() => setBundle(null)} />}
      {enrol && <EnrolTokenCard token={enrol} onDismiss={() => setEnrol(null)} />}

      <div className="stack">
        {gateways?.map((gw) => (
          <div className="row-card" key={gw.id}>
            <div>
              <div className="title mono">{gw.serial}</div>
              <div className="meta">
                {gw.controller_count} controller{gw.controller_count === 1 ? '' : 's'}
                {gw.enrolled && gw.cert_expires_at
                  ? ` · cert expires ${new Date(gw.cert_expires_at).toLocaleDateString()}`
                  : ''}
                {gw.agent_version ? ` · agent ${gw.agent_version}` : ''}
                {/* Teltonika's RED Art 3.3(d) / EN 18031-1 declaration covers RutOS 7.15 and later,
                    so the version is a compliance fact, not trivia. Never read is shown as such —
                    it is not the same as out of date. */}
                {' · '}
                {gw.firmware ? (
                  <span className={redCompliant(gw.firmware) === false ? 'fw-old' : undefined}>
                    RutOS {gw.firmware}
                    {redCompliant(gw.firmware) === false ? ' (pre-7.15)' : ''}
                  </span>
                ) : (
                  <span className="faint">firmware not read</span>
                )}
              </div>
              {gw.controllers.map((ctl) => (
                <Link
                  className="ctl-link"
                  // A Cerbo's system has its own page; the controller facia is Modbus-only.
                  to={
                    ctl.profile_id.startsWith('victron-')
                      ? `/victron/${ctl.id}`
                      : `/controllers/${ctl.id}`
                  }
                  key={ctl.id}
                >
                  ▸ {ctl.display_name ?? ctl.profile_id} — open panel
                </Link>
              ))}
              <CommissioningChain gw={gw} />
            </div>
            <div className="row-right">
              <GatewayState gw={gw} />
              {admin && (
                <>
                  <Button
                    className="btn btn-ghost btn-sm"
                    busy={issuingId === gw.id}
                    onClick={() => issue(gw)}
                  >
                    {gw.enrolled ? 'Re-issue cert' : 'Issue cert'}
                  </Button>
                  <Button
                    className="btn btn-ghost btn-sm"
                    busy={mintingId === gw.id}
                    onClick={() => mintToken(gw)}
                    title="Mint the token the router enrols itself with (replaces any earlier one)"
                  >
                    Router token
                  </Button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => decommissionGateway(gw)}
                    title="Decommission gateway"
                  >
                    ✕
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
        {gateways && gateways.length === 0 && (
          <div className="panel">
            <div className="empty">
              <h3>No gateways registered</h3>
              <p>Register this site’s RUT956 router to start bringing it online.</p>
            </div>
          </div>
        )}
      </div>

      {admin && (
        <div className="panel panel-pad" style={{ marginTop: 20 }}>
          <div className="section-label">Register a gateway</div>
          <form className="spread" onSubmit={addGateway} style={{ alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label htmlFor="serial">RUT956 serial</label>
              <input
                id="serial"
                className="input mono"
                placeholder="RUT956-XXXXXXXX"
                value={serial}
                onChange={(e) => setSerial(e.target.value)}
                required
              />
            </div>
            <Button type="submit" busy={adding}>
              Register
            </Button>
          </form>
        </div>
      )}
    </>
  );
}

/** One-time enrolment bundle: the material the operator installs on the edge device. */
function CertBundleCard({
  bundle,
  onDismiss,
}: {
  bundle: CertBundle;
  onDismiss: () => void;
}): JSX.Element {
  function download() {
    const payload = {
      gateway_id: bundle.gateway_id,
      fingerprint: bundle.fingerprint,
      not_after: bundle.not_after,
      ...bundle.bundle,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gateway-${bundle.gateway_id}-enrolment.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="panel panel-pad" style={{ marginBottom: 16, borderColor: 'var(--amber-deep)' }}>
      <div className="section-label" style={{ color: 'var(--amber)' }}>
        Certificate issued — save it now
      </div>
      <Banner kind="ok">
        This bundle includes the gateway’s private key and is shown <b>only once</b>. Download it
        and install it on the device; it cannot be retrieved again.
      </Banner>
      <dl className="kv">
        <dt>Gateway</dt>
        <dd className="mono">{bundle.gateway_id}</dd>
        <dt>Fingerprint</dt>
        <dd className="mono">{bundle.fingerprint}</dd>
        <dt>Expires</dt>
        <dd>{new Date(bundle.not_after).toLocaleString()}</dd>
      </dl>
      <div className="form-actions">
        <button className="btn btn-ghost" onClick={onDismiss}>
          Dismiss
        </button>
        <button className="btn btn-primary" onClick={download}>
          Download enrolment bundle
        </button>
      </div>
    </div>
  );
}
